import { useState } from 'react';
import { ScrollReveal } from '@/components/effects/ScrollReveal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, Users, TrendingUp, ArrowRight } from 'lucide-react';

const courses = [
  {
    title: 'Cybersecurity Fundamentals',
    track: 'Cybersecurity',
    duration: '12 weeks',
    salary: '£45-65k',
    level: 'Beginner',
    featured: true,
    skills: [
      { name: 'Network Security', pct: 85 },
      { name: 'Threat Analysis', pct: 78 },
      { name: 'Incident Response', pct: 72 },
    ],
    badgeStyle: { bg: 'rgba(248,113,113,0.10)', color: '#ff8888' },
  },
  {
    title: 'AI & Machine Learning',
    track: 'AI & ML',
    duration: '14 weeks',
    salary: '£55-80k',
    level: 'Intermediate',
    featured: true,
    skills: [
      { name: 'Python & TensorFlow', pct: 90 },
      { name: 'Neural Networks', pct: 82 },
      { name: 'Data Pipelines', pct: 75 },
    ],
    badgeStyle: { bg: 'rgba(192,132,252,0.10)', color: '#C084FC' },
  },
  {
    title: 'Data Analytics',
    track: 'Data',
    duration: '10 weeks',
    salary: '£40-55k',
    level: 'Beginner',
    featured: false,
    skills: [
      { name: 'SQL & Python', pct: 88 },
      { name: 'Visualisation', pct: 80 },
      { name: 'Statistical Analysis', pct: 70 },
    ],
    badgeStyle: { bg: 'rgba(74,222,128,0.10)', color: '#4ADE80' },
  },
  {
    title: 'Cloud Security Engineer',
    track: 'Cybersecurity',
    duration: '16 weeks',
    salary: '£60-85k',
    level: 'Advanced',
    featured: true,
    skills: [
      { name: 'AWS/Azure Security', pct: 92 },
      { name: 'IAM & Zero Trust', pct: 85 },
      { name: 'Container Security', pct: 78 },
    ],
    badgeStyle: { bg: 'rgba(248,113,113,0.10)', color: '#ff8888' },
  },
  {
    title: 'Career Accelerator',
    track: 'Career',
    duration: '8 weeks',
    salary: 'Salary Boost',
    level: 'All Levels',
    featured: false,
    skills: [
      { name: 'CV Engineering', pct: 95 },
      { name: 'Interview Prep', pct: 88 },
      { name: 'Networking', pct: 80 },
    ],
    badgeStyle: { bg: 'rgba(96,165,250,0.10)', color: '#60A5FA' },
  },
  {
    title: 'Penetration Testing',
    track: 'Cybersecurity',
    duration: '14 weeks',
    salary: '£50-75k',
    level: 'Intermediate',
    featured: false,
    skills: [
      { name: 'Ethical Hacking', pct: 88 },
      { name: 'Web App Testing', pct: 82 },
      { name: 'Reporting', pct: 75 },
    ],
    badgeStyle: { bg: 'rgba(248,113,113,0.10)', color: '#ff8888' },
  },
];

const CourseCard = ({ course, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <ScrollReveal delay={index * 0.065}>
      <div
        data-testid={`course-card-${index}`}
        className="relative p-6 rounded-[10px] transition-all duration-300 group h-full"
        style={{
          background: 'rgba(10,10,18,0.88)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(139,92,246,0.10)',
        }}
        onMouseEnter={(e) => {
          setIsHovered(true);
          e.currentTarget.style.borderColor = 'rgba(139,92,246,0.35)';
          e.currentTarget.style.transform = 'translateY(-5px)';
          e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(139,92,246,0.06)';
        }}
        onMouseLeave={(e) => {
          setIsHovered(false);
          e.currentTarget.style.borderColor = 'rgba(139,92,246,0.10)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* Track Badge */}
        <div className="flex items-center gap-2 mb-3">
          <Badge
            className="text-xs px-2.5 py-0.5 rounded-full font-body"
            style={{ background: course.badgeStyle.bg, color: course.badgeStyle.color, border: 'none' }}
          >
            {course.track}
          </Badge>
          {course.featured && (
            <Badge
              className="text-xs px-2.5 py-0.5 rounded-full font-body flex items-center gap-1"
              style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.20)', color: 'var(--a)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--a)', animation: 'pulseDot 2s ease-in-out infinite' }} />
              Active Training Track
            </Badge>
          )}
        </div>

        <h3 className="font-display text-lg font-bold mb-2" style={{ color: 'var(--white)' }}>
          {course.title}
        </h3>

        <div className="flex items-center gap-4 mb-4">
          <span className="flex items-center gap-1 font-body text-xs" style={{ color: 'var(--ts)' }}>
            <Clock size={12} /> {course.duration}
          </span>
          <span className="flex items-center gap-1 font-body text-xs" style={{ color: 'var(--ts)' }}>
            <TrendingUp size={12} /> {course.salary}
          </span>
          <span className="flex items-center gap-1 font-body text-xs" style={{ color: 'var(--ts)' }}>
            <Users size={12} /> {course.level}
          </span>
        </div>

        {/* Skill Bars */}
        <div className="space-y-3 mb-4">
          {course.skills.map((skill) => (
            <div key={skill.name}>
              <div className="flex justify-between mb-1">
                <span className="font-body text-xs" style={{ color: 'var(--tm)' }}>{skill.name}</span>
                <span className="font-body text-xs" style={{ color: 'var(--a)' }}>{skill.pct}%</span>
              </div>
              <div className="h-[3px] rounded-sm overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div
                  className="h-full rounded-sm"
                  style={{
                    width: isHovered ? `${skill.pct}%` : '0%',
                    background: 'linear-gradient(90deg, var(--violet), var(--a))',
                    transition: 'width 1.2s cubic-bezier(0.16,1,0.3,1)',
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 font-body text-xs" style={{ color: 'var(--ts)' }}>
          <span className="font-mono">Instructor-Led</span>
          <span style={{ color: 'var(--te)' }}>·</span>
          <span className="font-mono">Real Tools</span>
          <span style={{ color: 'var(--te)' }}>·</span>
          <span className="font-mono">Virtual Lab</span>
        </div>

        {/* Hover Enrol Button */}
        <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <a
            href="/pricing"
            data-testid={`course-enrol-${course.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="w-full inline-flex items-center justify-center h-9 rounded-[3px] font-body text-sm transition-colors duration-200"
            style={{ background: 'var(--a-glow)', color: 'var(--a)', border: '1px solid var(--a-border)' }}
          >
            Enrol Now <ArrowRight size={14} className="ml-1" />
          </a>
        </div>
      </div>
    </ScrollReveal>
  );
};

export const CoursesSection = () => {
  return (
    <section
      id="courses"
      data-testid="courses-section"
      className="py-14 sm:py-20"
      style={{ background: 'var(--l2)' }}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="font-body text-xs uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--violet)' }}>
              Acodia Learn
            </p>
            <h2
              data-testid="courses-heading"
              className="font-display font-extrabold tracking-[-0.03em] mb-4"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.8rem)', color: 'var(--white)' }}
            >
              Active Training Track
            </h2>
            <p className="font-body font-light text-base" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
              All sessions delivered face to face or online — your preference. One to one or small group.
              Flexible scheduling around your commitments.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {courses.map((course, i) => (
            <CourseCard key={course.title} course={course} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
